import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { smartFactureAPI, clientsAPI } from '../../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Badge } from '../../components/ui/badge';
import { useToast } from '../../components/ui/use-toast';
import TrialPaywall from '../../components/trial/TrialPaywall';
import useTrialGuard from '../../hooks/useTrialGuard';
import { 
  ArrowLeft, Plus, Trash2, FileText, Sparkles, Loader2, 
  Calculator, Calendar, User, Building2, Save, AlertTriangle,
  CheckCircle, Info, Shield, Users, LayoutTemplate
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CreateInvoicePage = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [aiGenerating, setAiGenerating] = useState(false);
  const [operationTypes, setOperationTypes] = useState({});
  const [vatRates, setVatRates] = useState({});
  const [clients, setClients] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  
  // Trial guard hook
  const { 
    showPaywall, 
    paywallReason, 
    paywallAction, 
    closePaywall, 
    handleTrialError 
  } = useTrialGuard();

  const [formData, setFormData] = useState({
    // Client info - Enhanced for compliance
    client_name: '',
    client_email: '',
    client_address: '',
    client_siren: '',
    client_siret: '',
    client_vat: '',
    
    // Invoice details
    invoice_date: new Date().toISOString().split('T')[0],
    delivery_date: new Date().toISOString().split('T')[0],
    due_date: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    currency: 'EUR',
    invoice_type: 'invoice',
    
    // French compliance fields
    operation_type: 'S2',
    payment_terms: 'Net 30',
    payment_method: 'virement',
    
    // Notes
    notes: '',
    
    // Items
    items: [
      { description: '', quantity: 1, unit_price: 0, tax_rate: 20 }
    ]
  });

  // Compliance validation state
  const [complianceWarnings, setComplianceWarnings] = useState([]);

  const currencySymbols = { EUR: '€', USD: '$', GBP: '£' };

  useEffect(() => {
    loadReferenceData();
  }, []);

  // Validate compliance as user types
  useEffect(() => {
    validateClientCompliance();
  }, [formData.client_siren, formData.client_vat, formData.operation_type]);

  const loadReferenceData = async () => {
    try {
      const token = localStorage.getItem('token');
      
      const [refResponse, clientsResponse, templatesResponse] = await Promise.all([
        smartFactureAPI.getOperationTypes(),
        clientsAPI.getAll(),
        fetch(`${API_URL}/api/templates/`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }).then(r => r.ok ? r.json() : { templates: [] })
      ]);
      
      setOperationTypes(refResponse.data.operation_types || {});
      setVatRates(refResponse.data.vat_rates || {});
      setClients(clientsResponse.data || []);
      setTemplates(templatesResponse.templates || []);
    } catch (error) {
      console.error('Failed to load reference data');
    }
  };

  const handleTemplateSelect = (templateId) => {
    const template = templates.find(t => t.id === templateId);
    if (template) {
      setSelectedTemplate(template);
      
      // Apply template items
      const templateItems = template.items?.map(item => ({
        description: item.description || '',
        quantity: item.quantity || 1,
        unit_price: item.unit_price || 0,
        tax_rate: item.vat_rate || 20
      })) || [{ description: '', quantity: 1, unit_price: 0, tax_rate: 20 }];
      
      setFormData(prev => ({
        ...prev,
        items: templateItems,
        notes: template.default_notes || prev.notes,
        payment_terms: `Net ${template.default_payment_terms || 30}`
      }));
      
      toast({
        title: "Modèle appliqué",
        description: `Le modèle "${template.name}" a été chargé`
      });
    }
  };

  const handleClientSelect = (clientId) => {
    const client = clients.find(c => c.id === clientId);
    if (client) {
      setFormData(prev => ({
        ...prev,
        client_name: client.name,
        client_email: client.email || '',
        client_address: client.address ? `${client.address}, ${client.postal_code || ''} ${client.city || ''}`.trim() : '',
        client_siren: client.siren || '',
        client_siret: client.siret || '',
        client_vat: client.vat_number || ''
      }));
      toast({
        title: 'Client sélectionné',
        description: `${client.name} a été chargé`
      });
    }
  };

  const validateClientCompliance = () => {
    const warnings = [];
    
    if (!formData.client_siren) {
      warnings.push({
        field: 'client_siren',
        message: 'Le SIREN du client est requis pour les factures B2B (obligatoire 2026)',
        severity: 'warning'
      });
    } else if (formData.client_siren.replace(/\s/g, '').length !== 9) {
      warnings.push({
        field: 'client_siren',
        message: 'Le SIREN doit contenir 9 chiffres',
        severity: 'error'
      });
    }
    
    if (!formData.client_vat && formData.client_siren) {
      warnings.push({
        field: 'client_vat',
        message: 'Le numéro de TVA intracommunautaire est recommandé',
        severity: 'info'
      });
    }
    
    setComplianceWarnings(warnings);
  };

  const addItem = () => {
    setFormData(prev => ({
      ...prev,
      items: [...prev.items, { description: '', quantity: 1, unit_price: 0, tax_rate: 20 }]
    }));
  };

  const removeItem = (index) => {
    if (formData.items.length > 1) {
      setFormData(prev => ({
        ...prev,
        items: prev.items.filter((_, i) => i !== index)
      }));
    }
  };

  const updateItem = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      items: prev.items.map((item, i) => 
        i === index ? { ...item, [field]: value } : item
      )
    }));
  };

  const calculateTotals = () => {
    const subtotal = formData.items.reduce((sum, item) => 
      sum + (item.quantity * item.unit_price), 0
    );
    const vatTotal = formData.items.reduce((sum, item) => 
      sum + (item.quantity * item.unit_price * item.tax_rate / 100), 0
    );
    const grandTotal = subtotal + vatTotal;
    return { subtotal, vatTotal, grandTotal };
  };

  const { subtotal, vatTotal, grandTotal } = calculateTotals();

  const handleAIGenerate = async () => {
    if (!formData.client_name) {
      toast({
        title: 'Information manquante',
        description: 'Veuillez d\'abord entrer le nom du client',
        variant: 'destructive'
      });
      return;
    }

    setAiGenerating(true);
    try {
      const response = await smartFactureAPI.generateWithAI({
        client_name: formData.client_name,
        context: formData.notes || 'Services de conseil professionnel'
      });
      
      if (response.data.items) {
        setFormData(prev => ({
          ...prev,
          items: response.data.items,
          notes: response.data.notes || prev.notes
        }));
        toast({
          title: 'Génération réussie',
          description: 'Les articles ont été générés par l\'IA',
        });
      }
    } catch (error) {
      toast({
        title: 'Erreur',
        description: 'Échec de la génération IA',
        variant: 'destructive'
      });
    } finally {
      setAiGenerating(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Check for blocking errors
    const errors = complianceWarnings.filter(w => w.severity === 'error');
    if (errors.length > 0) {
      toast({
        title: 'Erreurs de conformité',
        description: 'Veuillez corriger les erreurs avant de continuer',
        variant: 'destructive'
      });
      return;
    }
    
    setLoading(true);

    try {
      const invoiceData = {
        client_name: formData.client_name,
        client_email: formData.client_email || null,
        client_address: formData.client_address || null,
        client_siren: formData.client_siren || null,
        client_siret: formData.client_siret || null,
        client_vat: formData.client_vat || null,
        items: formData.items,
        currency: formData.currency,
        due_days: Math.ceil((new Date(formData.due_date) - new Date(formData.invoice_date)) / (1000 * 60 * 60 * 24)),
        delivery_date: formData.delivery_date || null,
        notes: formData.notes || null,
        invoice_type: formData.invoice_type,
        operation_type: formData.operation_type,
        payment_terms: formData.payment_terms,
        payment_method: formData.payment_method
      };

      const response = await smartFactureAPI.createInvoice(invoiceData);
      
      toast({
        title: 'Facture créée',
        description: `Facture ${response.data.invoice_number} créée avec succès`,
      });
      
      navigate('/dashboard/smartfacture');
    } catch (error) {
      // Check if it's a trial limit error
      if (handleTrialError(error, 'invoice_create')) {
        return; // Paywall will be shown by the hook
      }
      
      toast({
        title: 'Erreur',
        description: error.response?.data?.detail?.message || error.response?.data?.detail || 'Échec de la création de la facture',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const getComplianceIcon = () => {
    const errors = complianceWarnings.filter(w => w.severity === 'error');
    const warnings = complianceWarnings.filter(w => w.severity === 'warning');
    
    if (errors.length > 0) {
      return <AlertTriangle className="h-4 w-4 text-red-500" />;
    } else if (warnings.length > 0) {
      return <Info className="h-4 w-4 text-amber-500" />;
    }
    return <CheckCircle className="h-4 w-4 text-emerald-500" />;
  };

  return (
    <div className="space-y-6 max-w-6xl mx-auto" data-testid="create-invoice-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Button 
            variant="ghost" 
            onClick={() => navigate('/dashboard/smartfacture')}
            className="text-slate-600 hover:text-slate-900"
            data-testid="back-button"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Retour
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Nouvelle Facture</h1>
            <p className="text-slate-500 text-sm">Conforme Factur-X 2026</p>
          </div>
        </div>
        <div className="flex items-center space-x-3">
          <Badge variant="outline" className="flex items-center space-x-1.5 px-3 py-1.5">
            <Shield className="h-3.5 w-3.5" />
            <span>Factur-X</span>
          </Badge>
          <Button
            onClick={handleAIGenerate}
            disabled={aiGenerating}
            variant="outline"
            className="border-violet-200 text-violet-700 hover:bg-violet-50"
            data-testid="ai-generate-btn"
          >
            {aiGenerating ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Sparkles className="h-4 w-4 mr-2" />
            )}
            Générer avec l'IA
          </Button>
        </div>
      </div>

      {/* Compliance Alert */}
      {complianceWarnings.length > 0 && (
        <Card className="border-amber-200 bg-amber-50">
          <CardContent className="p-4">
            <div className="flex items-start space-x-3">
              {getComplianceIcon()}
              <div className="space-y-1">
                <p className="font-medium text-amber-800">Vérification conformité e-facturation 2026</p>
                <ul className="text-sm text-amber-700 space-y-1">
                  {complianceWarnings.map((warning, idx) => (
                    <li key={idx} className="flex items-center space-x-2">
                      <span className={`w-1.5 h-1.5 rounded-full ${
                        warning.severity === 'error' ? 'bg-red-500' : 
                        warning.severity === 'warning' ? 'bg-amber-500' : 'bg-blue-500'
                      }`} />
                      <span>{warning.message}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      <form onSubmit={handleSubmit}>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Template Selector */}
          {templates.length > 0 && (
            <div className="lg:col-span-3 p-4 bg-gradient-to-r from-indigo-50 to-purple-50 rounded-xl border border-indigo-100">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="p-2 bg-indigo-100 rounded-lg">
                    <LayoutTemplate className="h-5 w-5 text-indigo-600" />
                  </div>
                  <div>
                    <Label className="text-indigo-800 font-medium">Charger un modèle</Label>
                    <p className="text-xs text-indigo-600">Pré-remplir avec un modèle enregistré</p>
                  </div>
                </div>
                <Select onValueChange={handleTemplateSelect}>
                  <SelectTrigger className="w-64 bg-white border-indigo-200" data-testid="template-select">
                    <SelectValue placeholder="Choisir un modèle..." />
                  </SelectTrigger>
                  <SelectContent>
                    {templates.map(template => (
                      <SelectItem key={template.id} value={template.id}>
                        {template.name} {template.category ? `(${template.category})` : ''}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {selectedTemplate && (
                <div className="mt-3 text-sm text-indigo-700">
                  Modèle actif : <span className="font-medium">{selectedTemplate.name}</span>
                </div>
              )}
            </div>
          )}
          
          {/* Left Column - Client & Items */}
          <div className="lg:col-span-2 space-y-6">
            {/* Client Info */}
            <Card className="border-slate-200 shadow-sm">
              <CardHeader className="pb-4 border-b border-slate-100">
                <CardTitle className="text-base flex items-center space-x-2">
                  <Building2 className="h-5 w-5 text-blue-600" />
                  <span>Informations Destinataire</span>
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6 space-y-4">
                {/* Client Selector */}
                {clients.length > 0 && (
                  <div className="p-3 bg-blue-50 rounded-lg border border-blue-100">
                    <Label className="text-blue-800 text-sm font-medium flex items-center space-x-2 mb-2">
                      <Users className="h-4 w-4" />
                      <span>Sélectionner un client existant</span>
                    </Label>
                    <Select onValueChange={handleClientSelect}>
                      <SelectTrigger className="bg-white" data-testid="client-select">
                        <SelectValue placeholder="Choisir un client..." />
                      </SelectTrigger>
                      <SelectContent>
                        {clients.map(client => (
                          <SelectItem key={client.id} value={client.id}>
                            {client.name} {client.siren ? `(${client.siren})` : ''}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-blue-600 mt-1">
                      Ou remplissez manuellement ci-dessous
                    </p>
                  </div>
                )}

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="client_name" className="text-slate-700">Raison sociale *</Label>
                    <Input
                      id="client_name"
                      value={formData.client_name}
                      onChange={(e) => setFormData(prev => ({ ...prev, client_name: e.target.value }))}
                      placeholder="Nom de l'entreprise"
                      required
                      className="mt-1.5"
                      data-testid="client-name-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="client_email" className="text-slate-700">Email</Label>
                    <Input
                      id="client_email"
                      type="email"
                      value={formData.client_email}
                      onChange={(e) => setFormData(prev => ({ ...prev, client_email: e.target.value }))}
                      placeholder="contact@entreprise.com"
                      className="mt-1.5"
                    />
                  </div>
                </div>
                
                {/* SIREN / TVA Row - French compliance */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="client_siren" className="text-slate-700 flex items-center space-x-1">
                      <span>SIREN</span>
                      <Badge variant="outline" className="text-[10px] px-1.5 py-0 font-normal">
                        Requis 2026
                      </Badge>
                    </Label>
                    <Input
                      id="client_siren"
                      value={formData.client_siren}
                      onChange={(e) => setFormData(prev => ({ ...prev, client_siren: e.target.value.replace(/\D/g, '').slice(0, 9) }))}
                      placeholder="123 456 789"
                      className={`mt-1.5 ${
                        formData.client_siren && formData.client_siren.length !== 9 
                          ? 'border-red-300 focus:ring-red-500' 
                          : formData.client_siren ? 'border-emerald-300' : ''
                      }`}
                      data-testid="client-siren-input"
                    />
                    <p className="text-xs text-slate-500 mt-1">9 chiffres</p>
                  </div>
                  <div>
                    <Label htmlFor="client_vat" className="text-slate-700">N° TVA Intracommunautaire</Label>
                    <Input
                      id="client_vat"
                      value={formData.client_vat}
                      onChange={(e) => setFormData(prev => ({ ...prev, client_vat: e.target.value.toUpperCase() }))}
                      placeholder="FR12345678901"
                      className="mt-1.5"
                    />
                  </div>
                </div>
                
                <div>
                  <Label htmlFor="client_address" className="text-slate-700">Adresse</Label>
                  <Textarea
                    id="client_address"
                    value={formData.client_address}
                    onChange={(e) => setFormData(prev => ({ ...prev, client_address: e.target.value }))}
                    placeholder="123 Rue Example&#10;75001 Paris&#10;France"
                    className="mt-1.5"
                    rows={3}
                  />
                </div>
              </CardContent>
            </Card>

            {/* Invoice Items */}
            <Card className="border-slate-200 shadow-sm">
              <CardHeader className="pb-4 border-b border-slate-100">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base flex items-center space-x-2">
                    <FileText className="h-5 w-5 text-blue-600" />
                    <span>Lignes de facturation</span>
                  </CardTitle>
                  <Button type="button" variant="outline" size="sm" onClick={addItem} data-testid="add-item-btn">
                    <Plus className="h-4 w-4 mr-1" />
                    Ajouter une ligne
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="p-6 space-y-4">
                {formData.items.map((item, index) => (
                  <div 
                    key={index} 
                    className="p-4 bg-slate-50 rounded-lg space-y-3 relative group border border-slate-100"
                    data-testid={`invoice-item-${index}`}
                  >
                    {formData.items.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeItem(index)}
                        className="absolute top-3 right-3 p-1.5 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded transition-all"
                        data-testid={`remove-item-${index}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    )}
                    <div>
                      <Label className="text-slate-600 text-sm">Description *</Label>
                      <Textarea
                        value={item.description}
                        onChange={(e) => updateItem(index, 'description', e.target.value)}
                        placeholder="Description du produit ou service"
                        className="mt-1 bg-white"
                        rows={2}
                        required
                      />
                    </div>
                    <div className="grid grid-cols-4 gap-3">
                      <div>
                        <Label className="text-slate-600 text-sm">Quantité</Label>
                        <Input
                          type="number"
                          min="0.01"
                          step="0.01"
                          value={item.quantity}
                          onChange={(e) => updateItem(index, 'quantity', parseFloat(e.target.value) || 0)}
                          className="mt-1 bg-white"
                        />
                      </div>
                      <div>
                        <Label className="text-slate-600 text-sm">Prix HT ({currencySymbols[formData.currency]})</Label>
                        <Input
                          type="number"
                          min="0"
                          step="0.01"
                          value={item.unit_price}
                          onChange={(e) => updateItem(index, 'unit_price', parseFloat(e.target.value) || 0)}
                          className="mt-1 bg-white"
                        />
                      </div>
                      <div>
                        <Label className="text-slate-600 text-sm">TVA (%)</Label>
                        <Select
                          value={item.tax_rate.toString()}
                          onValueChange={(v) => updateItem(index, 'tax_rate', parseFloat(v))}
                        >
                          <SelectTrigger className="mt-1 bg-white">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="0">0% - Exonéré</SelectItem>
                            <SelectItem value="2.1">2.1% - Super réduit</SelectItem>
                            <SelectItem value="5.5">5.5% - Réduit</SelectItem>
                            <SelectItem value="10">10% - Intermédiaire</SelectItem>
                            <SelectItem value="20">20% - Normal</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="text-slate-600 text-sm">Total HT</Label>
                        <div className="mt-1 h-10 px-3 flex items-center bg-slate-100 rounded-md text-slate-700 font-medium">
                          {currencySymbols[formData.currency]}{(item.quantity * item.unit_price).toFixed(2)}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>

            {/* Notes */}
            <Card className="border-slate-200 shadow-sm">
              <CardHeader className="pb-4 border-b border-slate-100">
                <CardTitle className="text-base">Notes & Conditions</CardTitle>
              </CardHeader>
              <CardContent className="p-6">
                <Textarea
                  value={formData.notes}
                  onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                  placeholder="Conditions particulières, mentions légales additionnelles..."
                  rows={3}
                  className="bg-white"
                />
              </CardContent>
            </Card>
          </div>

          {/* Right Column - Details & Summary */}
          <div className="space-y-6">
            {/* Invoice Type & Operation */}
            <Card className="border-slate-200 shadow-sm">
              <CardHeader className="pb-4 border-b border-slate-100">
                <CardTitle className="text-base flex items-center space-x-2">
                  <Shield className="h-5 w-5 text-blue-600" />
                  <span>Type de document</span>
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6 space-y-4">
                <div>
                  <Label className="text-slate-700">Type</Label>
                  <Select
                    value={formData.invoice_type}
                    onValueChange={(v) => setFormData(prev => ({ ...prev, invoice_type: v }))}
                  >
                    <SelectTrigger className="mt-1.5">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="invoice">Facture</SelectItem>
                      <SelectItem value="quote">Devis</SelectItem>
                      <SelectItem value="credit_note">Avoir</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-slate-700 flex items-center space-x-1">
                    <span>Code opération</span>
                    <Badge variant="outline" className="text-[10px] px-1.5 py-0 font-normal">
                      Obligatoire
                    </Badge>
                  </Label>
                  <Select
                    value={formData.operation_type}
                    onValueChange={(v) => setFormData(prev => ({ ...prev, operation_type: v }))}
                  >
                    <SelectTrigger className="mt-1.5">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="S1">S1 - Livraison de biens</SelectItem>
                      <SelectItem value="S2">S2 - Prestation de services</SelectItem>
                      <SelectItem value="S3">S3 - Mixte (biens et services)</SelectItem>
                      <SelectItem value="A1">A1 - Acompte sur livraison</SelectItem>
                      <SelectItem value="A2">A2 - Acompte sur prestation</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </CardContent>
            </Card>

            {/* Dates & Payment */}
            <Card className="border-slate-200 shadow-sm">
              <CardHeader className="pb-4 border-b border-slate-100">
                <CardTitle className="text-base flex items-center space-x-2">
                  <Calendar className="h-5 w-5 text-blue-600" />
                  <span>Dates & Paiement</span>
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6 space-y-4">
                <div>
                  <Label className="text-slate-700">Date d'émission</Label>
                  <Input
                    type="date"
                    value={formData.invoice_date}
                    onChange={(e) => setFormData(prev => ({ ...prev, invoice_date: e.target.value }))}
                    className="mt-1.5"
                    data-testid="invoice-date-input"
                  />
                </div>
                <div>
                  <Label className="text-slate-700">
                    Date de prestation / livraison
                    <span className="ml-1 text-xs text-slate-500">(art. 242 nonies A CGI)</span>
                  </Label>
                  <Input
                    type="date"
                    value={formData.delivery_date}
                    onChange={(e) => setFormData(prev => ({ ...prev, delivery_date: e.target.value }))}
                    className="mt-1.5"
                    data-testid="delivery-date-input"
                  />
                </div>
                <div>
                  <Label className="text-slate-700">Date d'échéance</Label>
                  <Input
                    type="date"
                    value={formData.due_date}
                    onChange={(e) => setFormData(prev => ({ ...prev, due_date: e.target.value }))}
                    className="mt-1.5"
                    data-testid="due-date-input"
                  />
                </div>
                <div>
                  <Label className="text-slate-700">Conditions de paiement</Label>
                  <Select
                    value={formData.payment_terms}
                    onValueChange={(v) => setFormData(prev => ({ ...prev, payment_terms: v }))}
                  >
                    <SelectTrigger className="mt-1.5">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="immediate">Paiement immédiat</SelectItem>
                      <SelectItem value="Net 15">Net 15 jours</SelectItem>
                      <SelectItem value="Net 30">Net 30 jours</SelectItem>
                      <SelectItem value="Net 45">Net 45 jours</SelectItem>
                      <SelectItem value="Net 60">Net 60 jours</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-slate-700">Mode de paiement</Label>
                  <Select
                    value={formData.payment_method}
                    onValueChange={(v) => setFormData(prev => ({ ...prev, payment_method: v }))}
                  >
                    <SelectTrigger className="mt-1.5">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="virement">Virement bancaire</SelectItem>
                      <SelectItem value="carte">Carte bancaire</SelectItem>
                      <SelectItem value="cheque">Chèque</SelectItem>
                      <SelectItem value="prelevement">Prélèvement</SelectItem>
                      <SelectItem value="especes">Espèces</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-slate-700">Devise</Label>
                  <Select
                    value={formData.currency}
                    onValueChange={(v) => setFormData(prev => ({ ...prev, currency: v }))}
                  >
                    <SelectTrigger className="mt-1.5">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="EUR">EUR (€)</SelectItem>
                      <SelectItem value="USD">USD ($)</SelectItem>
                      <SelectItem value="GBP">GBP (£)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </CardContent>
            </Card>

            {/* Totals Summary */}
            <Card className="border-slate-200 shadow-sm bg-gradient-to-br from-slate-50 to-blue-50">
              <CardHeader className="pb-4 border-b border-slate-100">
                <CardTitle className="text-base flex items-center space-x-2">
                  <Calculator className="h-5 w-5 text-blue-600" />
                  <span>Récapitulatif</span>
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6 space-y-3">
                <div className="flex justify-between text-slate-600">
                  <span>Total HT</span>
                  <span className="font-medium">{currencySymbols[formData.currency]}{subtotal.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-slate-600">
                  <span>TVA</span>
                  <span className="font-medium">{currencySymbols[formData.currency]}{vatTotal.toFixed(2)}</span>
                </div>
                <div className="border-t border-slate-200 pt-3">
                  <div className="flex justify-between text-lg font-bold">
                    <span className="text-slate-900">Total TTC</span>
                    <span className="text-blue-600">{currencySymbols[formData.currency]}{grandTotal.toFixed(2)}</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Actions */}
            <div className="space-y-3">
              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white h-11"
                data-testid="create-invoice-submit"
              >
                {loading ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Save className="h-4 w-4 mr-2" />
                )}
                Créer la facture
              </Button>
              <Button
                type="button"
                variant="outline"
                className="w-full"
                onClick={() => navigate('/dashboard/smartfacture')}
              >
                Annuler
              </Button>
            </div>
          </div>
        </div>
      </form>
      
      {/* Trial Paywall Modal */}
      <TrialPaywall 
        isOpen={showPaywall} 
        onClose={closePaywall}
        reason={paywallReason}
        actionType={paywallAction}
      />
    </div>
  );
};

export default CreateInvoicePage;
